import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiFetch } from '../../lib/api';
import { addDays, isWeekend } from '../../lib/dates';
import { dmy, leaveLabel, type LeaveRequest } from './types';

/**
 * Leave requests: approve or reject what staff have asked for.
 *
 * In the legacy app this lived on the admin dashboard and posted to
 * Backend_timesheet::approve_ann_leave / reject_ann_leave. Approving writes a
 * leave entry and a calendar day for each weekday, then texts the staff member.
 */

interface Props {
    readonly open: boolean;
    readonly onClose: () => void;
    /** A decision was made, so anything showing leave or counts should reload. */
    readonly onDecided?: (message: string) => void;
}

function weekdays(from: string, to: string): number {
    let n = 0;
    for (let d = from; d <= to; d = addDays(d, 1)) {
        if (!isWeekend(d)) {
            n += 1;
        }
    }
    return n;
}

/** Used on the Dashboard (the Leave requests card) and the Timesheet page. Loads its own list. */
export function LeaveRequestsDialog({ open, onClose, onDecided }: Props): React.JSX.Element {
    const [requests, setRequests] = useState<readonly LeaveRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);
    const [tab, setTab] = useState<'pending' | 'recent'>('pending');
    const [confirm, setConfirm] = useState<{ id: number; action: 'approve' | 'reject' } | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setError(null);
            setWarning(null);
            setNotice(null);
            setConfirm(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        apiFetch<{ requests: readonly LeaveRequest[] }>('/timesheets/leave-requests')
            .then((r) => {
                if (!cancelled) {
                    setRequests(r.requests);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load leave requests');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, nonce]);

    const pending = useMemo(() => requests.filter((r) => r.status === 'pending'), [requests]);
    const recent = useMemo(
        () =>
            requests
                .filter((r) => r.status !== 'pending')
                .sort((a, b) => (b.decidedAt ?? '').localeCompare(a.decidedAt ?? '')),
        [requests],
    );
    const rows = tab === 'pending' ? pending : recent;

    function decide(id: number, action: 'approve' | 'reject'): void {
        setBusy(true);
        setError(null);
        setWarning(null);
        apiFetch<{ message: string; smsWarning: string | null }>(
            `/timesheets/leave-requests/${String(id)}/${action}`,
            { method: 'POST' },
        )
            .then((r) => {
                setWarning(r.smsWarning);
                setNotice(r.message);
                setNonce((n) => n + 1);
                onDecided?.(r.message);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'That did not work');
            })
            .finally(() => {
                setBusy(false);
                setConfirm(null);
            });
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: { elevation: 1 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                Leave Requests
                {loading ? <CircularProgress size={18} /> : null}
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={onClose} size="small" disabled={busy} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error !== null ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}
                {notice !== null ? (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        onClose={() => {
                            setNotice(null);
                        }}
                    >
                        {notice}
                    </Alert>
                ) : null}
                {warning !== null ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {warning}
                    </Alert>
                ) : null}

                <Tabs
                    value={tab}
                    onChange={(_e, v: 'pending' | 'recent') => {
                        setTab(v);
                        setConfirm(null);
                    }}
                    sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Tab value="pending" label={`Pending (${String(pending.length)})`} />
                    <Tab value="recent" label="Decided (last 60 days)" />
                </Tabs>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 640 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Staff Member</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>From</TableCell>
                                <TableCell>To</TableCell>
                                <TableCell align="right">Weekdays</TableCell>
                                <TableCell align="right">{tab === 'pending' ? 'Action' : 'Decision'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                        <Typography color="text.secondary">
                                            {tab === 'pending' ? 'No leave waiting for a decision.' : 'Nothing decided recently.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((r, idx) => (
                                    <TableRow key={r.id} sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.staffName ?? '—'}</TableCell>
                                        <TableCell>{leaveLabel(r.leaveType)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dmy(r.startsOn)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dmy(r.endsOn)}</TableCell>
                                        <TableCell align="right">{weekdays(r.startsOn, r.endsOn)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {r.status !== 'pending' ? (
                                                <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        color={r.status === 'approved' ? 'success' : 'error'}
                                                        label={r.status === 'approved' ? 'Approved' : 'Rejected'}
                                                    />
                                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                        {r.decidedBy ?? ''}
                                                        {r.decidedAt !== null ? ` ${dmy(r.decidedAt.slice(0, 10))}` : ''}
                                                    </Typography>
                                                </Stack>
                                            ) : confirm?.id === r.id ? (
                                                <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
                                                    <Typography sx={{ fontSize: '0.8rem' }}>
                                                        {confirm.action === 'approve' ? 'Approve and text them?' : 'Reject?'}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color={confirm.action === 'approve' ? 'success' : 'error'}
                                                        disabled={busy}
                                                        onClick={() => {
                                                            decide(r.id, confirm.action);
                                                        }}
                                                    >
                                                        {busy ? 'Saving...' : 'Yes'}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        disabled={busy}
                                                        onClick={() => {
                                                            setConfirm(null);
                                                        }}
                                                    >
                                                        No
                                                    </Button>
                                                </Stack>
                                            ) : (
                                                <Stack direction="row" gap={1} justifyContent="flex-end">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="success"
                                                        disabled={busy}
                                                        onClick={() => {
                                                            setConfirm({ id: r.id, action: 'approve' });
                                                        }}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        disabled={busy}
                                                        onClick={() => {
                                                            setConfirm({ id: r.id, action: 'reject' });
                                                        }}
                                                    >
                                                        Reject
                                                    </Button>
                                                </Stack>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography sx={{ mt: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                    Approving adds an 8-hour leave entry and a calendar day for every weekday in the range, and sends the
                    staff member an SMS.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={busy}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
